import React from 'react';
import { Box } from '@mui/material';
import ShimmerEffect from './ShimmerEffect';

// Generated by Copilot

/**
 * ShimmerLoader - A replacement for MiniLoader when using lazy loading
 * This component demonstrates how to use ShimmerEffect as a flexible loader
 */

interface ShimmerLoaderProps {
  /** Width of the shimmer effect */
  width?: string | number;
  /** Height of the shimmer effect */
  height?: string | number;
  /** Border radius for rounded corners */
  borderRadius?: string | number;
  /** Additional CSS class */
  className?: string;
  /** Variant type for different shapes */
  variant?: 'text' | 'rectangular' | 'circular' | 'rounded';
  /** Number of text lines (for text variant) */
  lines?: number;
  /** Loading text to display */
  loadingText?: string;
}

const ShimmerLoader: React.FC<ShimmerLoaderProps> = ({
  width = '100%',
  height = '24px',
  borderRadius = '4px',
  className = '',
  variant = 'rectangular',
  lines = 1,
  loadingText,
}) => {
  // For text variant with loading text
  if (variant === 'text' && loadingText) {
    return (
      <Box className={`shimmer-loader ${className}`}>
        <ShimmerEffect 
          variant="text" 
          lines={lines} 
          height={height}
          width={width}
        />
      </Box>
    );
  }

  // For loading text with shimmer background
  if (loadingText) {
    return (
      <ShimmerEffect 
        width={width}
        height={height}
        borderRadius={borderRadius}
        className={`shimmer-loader ${className}`}
      >
        <Box sx={{ 
          color: 'rgba(0, 0, 0, 0.4)',
          fontSize: '0.875rem',
          fontWeight: 500,
        }}>
          {loadingText}
        </Box>
      </ShimmerEffect>
    );
  }

  // Default shimmer without text
  return (
    <ShimmerEffect
      width={width}
      height={height}
      borderRadius={borderRadius}
      variant={variant}
      className={`shimmer-loader ${className}`}
    />
  );
};

export default ShimmerLoader;

// Usage examples:

/**
 * Basic Usage Examples:
 * 
 * // Replace MiniLoader with ShimmerLoader
 * <ShimmerLoader width="24px" height="24px" variant="circular" />
 * 
 * // For button loading state
 * <ShimmerLoader width="120px" height="48px" variant="rounded" />
 * 
 * // For text loading
 * <ShimmerLoader variant="text" lines={3} height="16px" />
 * 
 * // For card content loading
 * <ShimmerLoader width="100%" height="200px" borderRadius="8px" />
 * 
 * // With loading text
 * <ShimmerLoader 
 *   width="200px" 
 *   height="40px" 
 *   loadingText="Loading..." 
 *   variant="rounded" 
 * />
 * 
 * // Responsive - adjusts to parent container
 * <div style={{ width: '300px' }}>
 *   <ShimmerLoader /> // Will be 300px wide
 * </div>
 */
